import { useState } from "react";
import { MapPin, Copy, Navigation, Clock3 } from "lucide-react";
import { toast } from "sonner";
import { useSite } from "../context/SiteContext";
import { Reveal, SectionHeader } from "./Reveal";

const DAY_KEYS = ["Domingo", "Lunes", "Martes", "Miércoles", "Jueves", "Viernes", "Sábado"];

const fmt = (t) => {
    if (!t) return "";
    const [h, m] = t.split(":").map(Number);
    const h12 = h % 12 === 0 ? 12 : h % 12;
    return `${h12}:${String(m || 0).padStart(2, "0")} ${h < 12 ? "AM" : "PM"}`;
};

export default function HoursLocation() {
    const { content, t } = useSite();
    const [copied, setCopied] = useState(false);

    const hours = content?.hours || [];
    const ny = new Date(new Date().toLocaleString("en-US", { timeZone: "America/New_York" }));
    const todayName = DAY_KEYS[ny.getDay()];
    const nowMins = ny.getHours() * 60 + ny.getMinutes();
    const today = hours.find((h) => h.day === todayName);

    let isOpen = false;
    if (today && !today.closed && today.open && today.close) {
        const [oh, om] = today.open.split(":").map(Number);
        const [ch, cm] = today.close.split(":").map(Number);
        isOpen = nowMins >= oh * 60 + (om || 0) && nowMins < ch * 60 + (cm || 0);
    }

    const copyAddress = async () => {
        try {
            await navigator.clipboard.writeText(content?.address || "");
            setCopied(true);
            toast.success("Dirección copiada");
            setTimeout(() => setCopied(false), 2000);
        } catch {
            toast.error("No se pudo copiar");
        }
    };

    return (
        <section id="horario" data-testid="hours-section" className="relative py-24 sm:py-32 bg-[#0D0D0F]">
            <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16">
                <SectionHeader eyebrow={t.hours.eyebrow} title={t.hours.title} />

                <div className="mt-14 grid lg:grid-cols-2 gap-10 items-stretch">
                    <Reveal>
                        <div className="h-full rounded-3xl border border-gold/15 bg-surface p-8 sm:p-10">
                            <div className="flex items-center justify-between gap-4 flex-wrap">
                                <h3 className="font-serif text-2xl text-amber-100 flex items-center gap-3">
                                    <Clock3 size={22} className="text-gold" />
                                    {t.hours.openHours}
                                </h3>
                                <span
                                    data-testid="open-status-badge"
                                    className={`inline-flex items-center gap-2 rounded-full px-4 py-1.5 text-xs tracking-widest uppercase border ${
                                        isOpen
                                            ? "border-emerald-500/40 text-emerald-400 bg-emerald-500/10"
                                            : "border-red-500/40 text-red-400 bg-red-500/10"
                                    }`}
                                >
                                    <span className={`h-1.5 w-1.5 rounded-full ${isOpen ? "bg-emerald-400" : "bg-red-400"}`} />
                                    {isOpen ? t.hours.openNow : t.hours.closed}
                                </span>
                            </div>

                            <ul className="mt-8 divide-y divide-gold/10" data-testid="hours-list">
                                {hours.map((h) => {
                                    const isToday = h.day === todayName;
                                    return (
                                        <li
                                            key={h.day}
                                            data-testid={`hours-row-${h.day.toLowerCase()}`}
                                            className={`flex items-center justify-between py-3.5 px-3 -mx-3 rounded-lg transition-colors ${
                                                isToday ? "bg-gold/10 text-amber-50" : "text-zinc-400"
                                            }`}
                                        >
                                            <span className="flex items-center gap-2 text-sm tracking-wide">
                                                {t.days[h.day] || h.day}
                                                {isToday && (
                                                    <span className="text-[9px] uppercase tracking-[0.2em] text-gold border border-gold/40 rounded-full px-2 py-0.5">
                                                        {t.hours.today}
                                                    </span>
                                                )}
                                            </span>
                                            <span className="text-sm font-light">
                                                {h.closed ? <span className="text-red-400/80 italic">{t.hours.closed}</span> : `${fmt(h.open)} – ${fmt(h.close)}`}
                                            </span>
                                        </li>
                                    );
                                })}
                            </ul>
                        </div>
                    </Reveal>

                    <Reveal delay={0.12}>
                        <div className="h-full rounded-3xl border border-gold/15 overflow-hidden flex flex-col bg-surface">
                            <iframe
                                title={`Mapa de ${content?.business_name || "J&S Beauty Studio"}`}
                                src={`https://www.google.com/maps?q=${encodeURIComponent(content?.address || "")}&output=embed`}
                                className="w-full flex-1 min-h-[280px] map-dark border-0"
                                loading="lazy"
                                referrerPolicy="no-referrer-when-downgrade"
                                data-testid="location-map"
                            />
                            <div className="p-6 sm:p-8 flex flex-col sm:flex-row sm:items-center gap-4 justify-between">
                                <div className="flex items-start gap-3" data-testid="location-address">
                                    <MapPin size={20} className="text-gold shrink-0 mt-0.5" />
                                    <div>
                                        <p className="text-amber-50 font-light">{content?.address}</p>
                                        <p className="text-xs text-zinc-500 mt-1">{t.hours.city}</p>
                                    </div>
                                </div>
                                <div className="flex gap-3 shrink-0">
                                    <button
                                        onClick={copyAddress}
                                        data-testid="copy-address-button"
                                        className="inline-flex items-center gap-2 rounded-full border border-gold/30 px-4 py-2 text-xs uppercase tracking-widest text-gold-light hover:bg-gold/10 transition-colors"
                                    >
                                        <Copy size={13} />
                                        {copied ? t.hours.copied : t.hours.copy}
                                    </button>
                                    <a
                                        href={content?.map_link || `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(content?.address || "")}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        data-testid="directions-link"
                                        className="inline-flex items-center gap-2 rounded-full bg-gold-gradient px-4 py-2 text-xs uppercase tracking-widest text-black font-semibold hover:shadow-[0_0_25px_rgba(212,175,55,0.35)] transition-shadow"
                                    >
                                        <Navigation size={13} />
                                        {t.hours.directions}
                                    </a>
                                </div>
                            </div>
                        </div>
                    </Reveal>
                </div>
            </div>
        </section>
    );
}
